/**
 * Состояние экранов задач (DEV-2040): отбор живёт ТОЛЬКО в URL, вид — только в
 * localStorage.
 *
 * Разделение принципиальное, а не техническое. Отбор в URL — потому что срез
 * пересылают ссылкой и он обязан пережить F5; и потому что восстановленный из
 * localStorage фильтр невидим — человек открывает список, не видит задачу и
 * не знает, что смотрит через вчерашнее сито. Вид (сортировка, группировка,
 * свёрнутость) — привычка, а не срез: им не делятся ссылкой, и его не страшно
 * восстановить молча.
 *
 * `?issue=` (панель задачи) в белом списке НЕ значится нарочно: открытие peek
 * меняет адрес, но не отбор, и перезагружать список из-за него нельзя.
 */
import { watch } from 'vue'
import { useRoute, useRouter, type LocationQuery, type LocationQueryValue } from 'vue-router'

import { emptyFilters, type IssueFilters } from './filters'

import type { IssueSort, IssueSortField, ShowTab } from './api'
import type { V1IssueType, V1Priority } from '@/api/clients/tracker-issue'

/** Оси группировки списка; 'none' — плоская таблица со страницами */
export type GroupBy = 'status' | 'assignee' | 'priority' | 'type' | 'none'

/** Порядок в списке выбора: статус первым — он же вид по умолчанию */
export const GROUP_OPTIONS: GroupBy[] = ['status', 'assignee', 'priority', 'type', 'none']

/**
 * Ключи отбора в адресе. Общая часть с доской — assignee/reporter/type/
 * priority/q: она переживает переключение вида. column и tab — только про
 * список; доска показывает на них чип «часть отбора не действует».
 */
const LIST_KEYS = ['assignee', 'reporter', 'type', 'priority', 'q', 'column', 'tab'] as const

/** Что из отбора понимает доска */
export const SHARED_KEYS = ['assignee', 'reporter', 'type', 'priority', 'q'] as const

const first = (value: LocationQueryValue | LocationQueryValue[] | undefined): string =>
  String((Array.isArray(value) ? value[0] : value) ?? '')

const list = (value: LocationQueryValue | LocationQueryValue[] | undefined): string[] =>
  first(value)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

const TABS: ShowTab[] = ['open', 'done', 'all']

/** Отбор и таб из адреса. Незнакомые значения молча отбрасываются: кривая
 *  ссылка должна открыть список, а не белый экран. */
export const filtersFromQuery = (
  query: LocationQuery,
): { filters: IssueFilters; tab: ShowTab | null } => {
  const filters = emptyFilters()

  filters.assigneeIds = list(query.assignee)
  filters.reporterIds = list(query.reporter)
  filters.types = list(query.type) as V1IssueType[]
  filters.priorities = list(query.priority) as V1Priority[]
  filters.columnIds = list(query.column)
  filters.search = first(query.q)

  const tab = first(query.tab) as ShowTab

  return { filters, tab: TABS.includes(tab) ? tab : null }
}

/** Отбор в query-часть адреса; пустые ключи не пишутся вовсе */
export const filtersToQuery = (filters: IssueFilters, tab: ShowTab): LocationQuery => {
  const query: LocationQuery = {}

  if (filters.assigneeIds.length) query.assignee = filters.assigneeIds.join(',')
  if (filters.reporterIds.length) query.reporter = filters.reporterIds.join(',')
  if (filters.types.length) query.type = filters.types.join(',')
  if (filters.priorities.length) query.priority = filters.priorities.join(',')
  if (filters.columnIds.length) query.column = filters.columnIds.join(',')
  if (filters.search.trim()) query.q = filters.search.trim()
  // Дефолтный таб адрес не засоряет: чистый список — чистая ссылка.
  if (tab !== 'open') query.tab = tab

  return query
}

/** Общая с доской часть адреса — её переносит переключатель вида */
export const sharedQueryOf = (query: LocationQuery): LocationQuery => {
  const shared: LocationQuery = {}

  for (const key of SHARED_KEYS) {
    if (query[key] !== undefined) shared[key] = query[key]
  }

  return shared
}

/** Есть ли в адресе часть отбора, которую доска не применяет */
export const hasListOnlyQuery = (query: LocationQuery): boolean =>
  query.column !== undefined || query.tab !== undefined

// ─────────────────────────────── вид ───────────────────────────────

/** Настройки вида списка; переживают перезагрузку, но не пересылаются ссылкой */
export interface ListViewPrefs {
  sort: IssueSort
  grouping: GroupBy
  /** Таб «Показ» — тоже привычка: в отличие от фильтров он виден чипом.
   *  Таб из адреса важнее — ссылка «Все закрытые →» обязана открыть закрытые. */
  tab: ShowTab
  /** Свёрнутые секции; ключи — серверные ключи групп */
  collapsed: string[]
  /** Однократный хинт о новом дефолте сортировки уже показан */
  sortHintSeen: boolean
}

export const defaultViewPrefs = (): ListViewPrefs => ({
  // Свежие правки сверху — ответ на «задачи добавляются, а найти невозможно»:
  // прежний дефолт key ASC держал древние задачи первыми (DEV-2041).
  sort: { field: 'updated_at', desc: true },
  grouping: 'status',
  tab: 'open',
  collapsed: [],
  sortHintSeen: false,
})

const PREFS_PREFIX = 'tracker.issues.view.'

const SORT_FIELDS: IssueSortField[] = [
  'key',
  'priority',
  'due_date',
  'created_at',
  'updated_at',
  'completed_at',
]

/**
 * Читает вид из localStorage. Кривые и старые значения падают в дефолт
 * по-полю: одна испорченная запись не должна стирать остальные привычки.
 */
export const loadViewPrefs = (projectKey: string): ListViewPrefs => {
  const prefs = defaultViewPrefs()

  try {
    const raw = localStorage.getItem(PREFS_PREFIX + projectKey)

    if (!raw) return prefs

    const parsed = JSON.parse(raw) as Partial<ListViewPrefs>

    if (parsed.sort && SORT_FIELDS.includes(parsed.sort.field)) {
      prefs.sort = { field: parsed.sort.field, desc: Boolean(parsed.sort.desc) }
    }

    if (parsed.grouping && GROUP_OPTIONS.includes(parsed.grouping)) prefs.grouping = parsed.grouping
    if (parsed.tab && TABS.includes(parsed.tab)) prefs.tab = parsed.tab
    if (Array.isArray(parsed.collapsed)) prefs.collapsed = parsed.collapsed.map(String)

    prefs.sortHintSeen = Boolean(parsed.sortHintSeen)
  } catch {
    // localStorage бывает выключен; вид тогда просто не запоминается.
  }

  return prefs
}

/** Пишет вид; сброс пишет дефолт, а не удаляет ключ — «пусто» тоже выбор */
export const saveViewPrefs = (projectKey: string, prefs: ListViewPrefs): void => {
  try {
    localStorage.setItem(PREFS_PREFIX + projectKey, JSON.stringify(prefs))
  } catch {
    // Некуда писать — не запомнится, и только.
  }
}

// ─────────────────────────── связка с адресом ───────────────────────────

interface ListStateStore {
  filters: IssueFilters
  tab: ShowTab
  applyQueryState: (filters: IssueFilters, tab: ShowTab | null) => Promise<void> | void
}

/**
 * Двусторонняя связка стора с адресом. URL всегда побеждает: он — единственный
 * источник отбора, и пересланная ссылка обязана открыть ровно присланный срез.
 * Изменение отбора в сторе переписывает адрес replace'ом — отбор не должен
 * наматывать историю браузера на каждый чип.
 */
export const useIssueListState = (store: ListStateStore): void => {
  const route = useRoute()
  const router = useRouter()

  const applyFromRoute = async () => {
    const { filters, tab } = filtersFromQuery(route.query)
    const current = filtersToQuery(store.filters, store.tab)
    const incoming = filtersToQuery(filters, tab ?? 'open')

    // Сторож по сериализованной форме: смена `?issue=` и прочих чужих ключей
    // не должна перезагружать список.
    if (JSON.stringify(current) === JSON.stringify(incoming)) return

    await store.applyQueryState(filters, tab)
  }

  const writeToRoute = async () => {
    const preserved: LocationQuery = {}

    for (const [key, value] of Object.entries(route.query)) {
      if (!(LIST_KEYS as readonly string[]).includes(key)) preserved[key] = value
    }

    const next = { ...preserved, ...filtersToQuery(store.filters, store.tab) }

    if (JSON.stringify(next) === JSON.stringify(route.query)) return

    await router.replace({ query: next })
  }

  watch(
    () => route.query,
    () => void applyFromRoute(),
  )

  watch(
    () => [store.filters, store.tab],
    () => void writeToRoute(),
    { deep: true },
  )

  void applyFromRoute()
}
